import { BreakoutButton } from 'components/design-system/BreakoutButton'
import { useState } from 'react'
import { useTranslationTyped } from 'i18n/i18n'
import { ConfigureScoreMapping } from './ConfigureScoreMapping'
import type { ScaleDef, ScaleId } from './customRubricsConfig'

/**
 * Tabbed score-mapping editor: one tab per scale (Bloom's, DQR, Pass/Fail) over
 * a shared ConfigureScoreMapping editor. Used both for the per-assignment score
 * mapping column (the present scales) and the saved-defaults dialog (all
 * scales). Read-only when `onChange` is omitted — the tabs still switch so every
 * scale can be viewed.
 */
export function ScoreMappingScales({
  scales,
  values,
  onChange,
  disabled,
}: {
  scales: ScaleDef[]
  values: Record<ScaleId, Record<string, number>>
  onChange?: (scaleId: ScaleId, next: Record<string, number>) => void
  disabled?: boolean
}) {
  const { tt } = useTranslationTyped()

  const [activeScale, setActiveScale] = useState<ScaleId | undefined>(
    scales[0]?.id
  )
  // Fall back to the first scale when the active one isn't in the current set.
  const activeScaleDef = scales.find((s) => s.id === activeScale) ?? scales[0]
  if (!activeScaleDef) return null

  return (
    <div className="flex w-full flex-col gap-3">
      {scales.length > 1 && (
        <div className="flex w-full flex-row gap-2 rounded-2xl border border-outline-light bg-white p-2">
          {scales.map((scale) => (
            <BreakoutButton
              key={scale.id}
              className="min-w-0! flex-1"
              size="medium"
              kind={scale.id === activeScaleDef.id ? 'accent' : 'secondary'}
              onClick={() => setActiveScale(scale.id)}
              data-testid={`score-mapping-tab-${scale.id}`}
            >
              {tt.instructor_library[scale.labelKey]()}
            </BreakoutButton>
          ))}
        </div>
      )}

      <ConfigureScoreMapping
        levels={activeScaleDef.levels}
        rubricType={activeScaleDef.rubricTypes[0]}
        values={values[activeScaleDef.id]}
        disabled={disabled || !onChange}
        onChange={(next) => onChange?.(activeScaleDef.id, next)}
      />
    </div>
  )
}
